/**
 * 단위 변환 관련 유틸 함수 모듈
 * @module core/number/conversion
 */
import { roundToDigit, roundToDigitNumber } from './format';

/**
 * 바이트 단위를 KiB, MiB 등으로 변환
 */
export const convertBytesToUnits = (bytes: number, digits = 0) => {
  if ((!!bytes || bytes === 0) && Number.MAX_SAFE_INTEGER > bytes) {
    const unitList = ['Byte', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
    const maxUnitIdx = unitList.length - 1;
    const ONE_UNIT = 1024;
    let unitIdx = 0;
    let result;
    const dfs = (currUnitIdx: number, size: number) => {
      if (currUnitIdx > maxUnitIdx) {
        console.log('너무 큰 용량입니다.');
        return;
      }
      if (size < ONE_UNIT) {
        result = size;
        unitIdx = currUnitIdx;
      } else {
        dfs(currUnitIdx + 1, size / ONE_UNIT);
      }
    };
    dfs(unitIdx, bytes);
    return `${roundToDigit(result, digits)} ${unitList[unitIdx]}`;
  }
  return '';
};

/**
 * MB를 GB로 변환
 */
export const convertMbToGb = (data: number, digits = 3): number =>
  roundToDigitNumber(data / 1024, digits);

/**
 * 문자열에서 뒤의 0을 제거한 문자열을 반환
 * @param str - 문자열
 * @returns 문자열에서 뒤의 0을 제거한 문자열
 */
export const removeTrailingZero = (str: string): string => str.replace(/\.0+$|(\.\d*?)0+$/, '$1');

const COMPACT_UNITS = [
  { value: 1e15, suffix: 'P' },
  { value: 1e12, suffix: 'T' },
  { value: 1e9, suffix: 'G' },
  { value: 1e6, suffix: 'M' },
  { value: 1e3, suffix: 'K' },
];

/**
 * 숫자를 compact 단위로 변환 (예: 1.2K, 3.4M)
 * @param value - 숫자
 * @param { decimalPoint?: number; trailingZero?: boolean } - 소수점 자리수와 뒤의 0을 제거할지 여부를 지정하는 객체
 * @returns 숫자를 compact 단위로 변환한 문자열
 */
export const formatCompactNumber = (
  value: number,
  { decimalPoint = 0, trailingZero = true }: { decimalPoint?: number; trailingZero?: boolean } = {},
): string => {
  if (Number.isNaN(value)) {
    return 'NaN';
  }

  if (value === Infinity) {
    return '∞';
  }

  if (value === -Infinity) {
    return '-∞';
  }

  const formatNumber = (num: number): string => {
    const fixed = num.toFixed(decimalPoint);
    return trailingZero ? removeTrailingZero(fixed) : fixed;
  };

  const absValue = Math.abs(value);

  for (let i = 0; i < COMPACT_UNITS.length; i += 1) {
    const unit = COMPACT_UNITS[i];

    if (absValue >= unit.value) {
      const divided = value / unit.value;
      const rounded = Number(divided.toFixed(decimalPoint));

      // 반올림 결과가 1000이 되면 상위 단위로 승격
      if (Math.abs(rounded) >= 1000 && i > 0) {
        const upperUnit = COMPACT_UNITS[i - 1];
        return `${formatNumber(value / upperUnit.value)}${upperUnit.suffix}`;
      }

      return `${formatNumber(divided)}${unit.suffix}`;
    }
  }

  return formatNumber(value);
};

// 기존 코드 호환을 위해 유지
export function convertNumberToUnits(
  value: number,
  digits?: number | null | undefined,
): string | number;
export function convertNumberToUnits<T>(value: T, digits?: number | null | undefined): T;
export function convertNumberToUnits<T>(
  value: T,
  digits?: number | null | undefined,
): string | number | T {
  if (typeof value !== 'number') {
    return value;
  }

  return formatCompactNumber(value, {
    decimalPoint: digits ?? 0,
    trailingZero: true,
  });
}

/**
 * bps ~ Tbps 까지 단위 변환
 */
export const convertBpsToUnit = (inputNumber: number): string => {
  if (!inputNumber) return '';
  let value = inputNumber;
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  let perCheck = 0;
  while (value >= 1000 && perCheck < units.length - 1) {
    value /= 1000;
    perCheck++;
  }
  return `${value.toFixed(2)} ${units[perCheck]}`;
};

/**
 * ms, sec, min 등 시간 단위 변환
 */
export const convertTimeToUnits = (millisecond: number, digits = 1) => {
  if ((!!millisecond || millisecond === 0) && Number.MAX_SAFE_INTEGER > millisecond) {
    const unitList = ['ms', 'sec', 'min'];
    const maxUnitIdx = unitList.length - 1;
    const MS_UNIT = 1000;
    const TIME_UNIT = 60;
    let unitIdx = 0;
    let result;
    const dfs = (currUnitIdx: number, ms: number) => {
      if (currUnitIdx > maxUnitIdx) {
        console.log('너무 큰 숫자입니다.');
        return;
      }
      if (ms < TIME_UNIT && currUnitIdx > 0) {
        result = ms;
        unitIdx = currUnitIdx;
      } else if (ms >= MS_UNIT && currUnitIdx === 0) {
        dfs(currUnitIdx + 1, ms / MS_UNIT);
      } else {
        dfs(currUnitIdx + 1, ms / TIME_UNIT);
      }
    };
    dfs(unitIdx, millisecond);
    if (!unitIdx) {
      return `${result} ms`;
    }
    return `${roundToDigit(result, digits)} ${unitList[unitIdx]}`;
  }
  return '';
};

/**
 * ms → sec 변환
 */
export const convertMsToSec = (
  millisecond: number | string | null,
  options: { digits?: number; unit?: boolean } = { unit: false },
) => {
  if (!millisecond && millisecond !== 0) {
    return '';
  }
  const { digits = 3, unit = false } = options;
  const convertedValue = roundToDigit(+millisecond / 1000, digits);
  const unitValue = unit ? ' sec' : '';
  return convertedValue ? `${convertedValue}${unitValue}` : (millisecond as string);
};

/**
 * sec → ms 변환
 */
export const convertSecToMs = (
  sec: number | string,
  options: { digits?: number; unit?: boolean } = { digits: 3, unit: false },
) => {
  if (!sec && sec !== 0) {
    return '';
  }
  const { digits = 3, unit = false } = options;
  const convertedValue = roundToDigit(+sec * 1000, digits);
  const unitValue = unit ? ' ms' : '';
  return convertedValue ? `${convertedValue}${unitValue}` : (sec as string);
};

/**
 * ms(최소) ~ 사용자 지정(최대)로 시간 단위 변환
 */
export const convertTimeToUnitsWithMaxUnit = (
  millisecond: number,
  digits = 1,
  maxUnit: 'ms' | 'sec' | 'min' = 'min',
) => {
  if ((!!millisecond || millisecond === 0) && Number.MAX_SAFE_INTEGER > millisecond) {
    const unitList = ['ms', 'sec', 'min'];
    const maxUnitIdx = unitList.indexOf(maxUnit);
    const MS_UNIT = 1000;
    const TIME_UNIT = 60;
    let unitIdx = 0;
    let result = millisecond;
    const dfs = (currUnitIdx: number, ms: number) => {
      if (currUnitIdx > maxUnitIdx) {
        return;
      }
      result = ms;
      unitIdx = currUnitIdx;
      if (currUnitIdx === 0) {
        if (ms >= MS_UNIT) {
          dfs(currUnitIdx + 1, ms / MS_UNIT);
        }
      } else if (ms >= TIME_UNIT) {
        dfs(currUnitIdx + 1, ms / TIME_UNIT);
      }
    };
    dfs(unitIdx, millisecond);
    return {
      convertedValue: roundToDigit(result, digits),
      unit: unitList[unitIdx],
    };
  }
  return {
    convertedValue: '',
    unit: '',
  };
};

/**
 * ms를 동적으로 단위 변환 (ms, s, m, h, d)
 */
export const convertMsToDynamicTime = (
  ms: number,
  digits = 1,
  unitList = ['ms', 's', 'm', 'h', 'd'],
) => {
  if ((!!ms || ms === 0) && Number.MAX_SAFE_INTEGER > ms) {
    const MS_TO_1_SEC = 1000;
    const SEC_TO_1_MIN = 60;
    const MIN_TO_1_HOUR = 60;
    const HOUR_TO_1_DAY = 24;
    if (ms < MS_TO_1_SEC) {
      return `${roundToDigit(ms, digits)} ${unitList[0]}`;
    }
    if (ms < MS_TO_1_SEC * SEC_TO_1_MIN) {
      return `${roundToDigit(ms / MS_TO_1_SEC, digits)} ${unitList[1]}`;
    }
    if (ms < MS_TO_1_SEC * SEC_TO_1_MIN * MIN_TO_1_HOUR) {
      return `${roundToDigit(ms / (MS_TO_1_SEC * SEC_TO_1_MIN), digits)} ${unitList[2]}`;
    }
    if (ms < MS_TO_1_SEC * SEC_TO_1_MIN * MIN_TO_1_HOUR * HOUR_TO_1_DAY) {
      return `${roundToDigit(ms / (MS_TO_1_SEC * SEC_TO_1_MIN * MIN_TO_1_HOUR), digits)} ${
        unitList[3]
      }`;
    }
    return `${roundToDigit(
      ms / (MS_TO_1_SEC * SEC_TO_1_MIN * MIN_TO_1_HOUR * HOUR_TO_1_DAY),
      digits,
    )} ${unitList[4]}`;
  }
  return '';
};

/**
 * 최소값과 최대값 사이의 값을 퍼센트로 환산하는 함수
 * @param min - 최소값
 * @param max - 최대값
 * @param value - 값
 * @returns - 퍼센트
 */
export const getPercentageBetweenMinMax = (
  min: number | null,
  max: number | null,
  value: number | null,
): number | undefined => {
  if (min === null || max === null || value === null) {
    return undefined;
  }

  if (value < min) {
    return 0;
  }

  if (value > max) {
    return 100;
  }

  return ((value - min) / (max - min)) * 100;
};
